/* Mocked in-memory implementation of OnboardingClient for local development and UI wiring. */

import { IOnboardingClient } from './IOnboardingClient';
import {
  OnboardingPageData,
  UserFranchiseList,
  RetailerListModel,
  RetailerIdentifierModel,
  OnboardingCataloguesModel,
  RetailerOnboardingDataModel,
  RetailerEdnModel,
  RetailerHeaderModel,
  RetailerDocumentModel,
  Int32StringKeyValuePair,
} from './ofdTypes';

/* ---- Mock Data ---- */

const mockPalletSizes: Int32StringKeyValuePair[] = [
  { key: 1, value: 'Euro Pallet (1200x800mm)' },
  { key: 2, value: 'UK Pallet (1200x1000mm)' },
  { key: 3, value: 'Half Pallet (800x600mm)' },
  { key: 4, value: 'Quarter Pallet (600x400mm)' },
];

const mockContainerSizes: Int32StringKeyValuePair[] = [
  { key: 1, value: '20ft Container' },
  { key: 2, value: '40ft Container' },
  { key: 3, value: '40ft High Cube' },
  { key: 4, value: 'LCL (Less than Container Load)' },
];

const mockDocumentSkeleton: RetailerDocumentModel[] = [
  {
    documentId: 1,
    category: 'Legal',
    header: 'Business Registration Certificate',
    uploadRequired: true,
    fieldRequired: true,
    fileExists: false,
    approved: false,
    delete: false,
  },
  {
    documentId: 2,
    category: 'Legal',
    header: 'VAT Registration',
    uploadRequired: true,
    fieldRequired: true,
    fileExists: false,
    approved: false,
    delete: false,
  },
  {
    documentId: 3,
    category: 'Compliance',
    header: 'Food Safety Certificate',
    uploadRequired: true,
    fieldRequired: false,
    fileExists: false,
    approved: false,
    delete: false,
  },
  {
    documentId: 4,
    category: 'Financial',
    header: 'Bank Statement (Last 3 months)',
    uploadRequired: true,
    fieldRequired: true,
    fileExists: false,
    approved: false,
    delete: false,
  },
  {
    documentId: 5,
    category: 'Insurance',
    header: 'Public Liability Insurance',
    uploadRequired: false,
    fieldRequired: false,
    fileExists: false,
    approved: false,
    delete: false,
  },
];

const mockRetailers: RetailerOnboardingDataModel[] = [
  {
    retailerId: 1,
    statusId: 2, // Active
    name: 'Dublin Delights Café',
    addressLine1: '123 O\'Connell Street',
    addressLine2: 'Suite 4A',
    city: 'Dublin',
    postalCode: 'D01 F5P2',
    country: 'Ireland',
    region: 'Leinster',
    latitude: 53.3498,
    longitude: -6.2603,
    vatnumber: 'IE1234567T',
    awrsnumber: 'AWRS00012345',
    eorinumber: 'IE1234567890',
    parentCompany: 'Dublin Food Group Ltd',
    directors: 'John Murphy, Mary O\'Brien',
    progressStage: true,
    franchiseName: 'CFA',
    contactDetails: {
      name: 'John Murphy',
      jobTitle: 'General Manager',
      email: 'john.murphy@dublindelights.ie',
      phoneNumber: '+353 1 234 5678',
      interestReason: 'Expanding beverage menu',
      rangeRequirements: 'Coffee, Tea, Specialty Drinks',
      serviceNeeds: 'Weekly deliveries, Cold chain',
    },
    configuration: {
      storeId: 'DUB001',
      externalIdentifier: 'EXT-DUB-001',
      containerSizeId: 4,
      creditLimit: 25000,
      minContainerFillPercentage: 70,
      catalogueId: 1,
      palletSizeId: 1,
      leadTime: 5,
      orderCategories: [1, 2, 3],
      catalogues: [1, 2],
    },
    currentDocuments: [
      { ...mockDocumentSkeleton[0], fileExists: true, fileName: 'business_reg.pdf', approved: true },
      { ...mockDocumentSkeleton[1], fileExists: true, fileName: 'vat_cert.pdf', approved: true },
      { ...mockDocumentSkeleton[2], fileExists: true, fileName: 'food_safety.pdf', approved: false },
    ],
    deliveryLocations: [
      {
        id: 1,
        name: 'Main Store',
        addressLine1: '123 O\'Connell Street',
        city: 'Dublin',
        postalCode: 'D01 F5P2',
        country: 'Ireland',
        contactName: 'John Murphy',
        contactNumber: '+353 1 234 5678',
      },
    ],
    deliveryMethods: [
      { shippingMethodId: 1, name: 'Standard Delivery', enabled: true, isDefault: true },
      { shippingMethodId: 2, name: 'Express Delivery', enabled: true, isDefault: false },
    ],
  },
  {
    retailerId: 2,
    statusId: 1, // Pending
    name: 'Cork Coffee Corner',
    addressLine1: '45 Patrick Street',
    city: 'Cork',
    postalCode: 'T12 AB34',
    country: 'Ireland',
    region: 'Munster',
    latitude: 51.8985,
    longitude: -8.4756,
    vatnumber: 'IE9876543T',
    progressStage: false,
    franchiseName: 'CFA',
    contactDetails: {
      name: 'Sarah Collins',
      jobTitle: 'Owner',
      email: 'sarah@corkcoffee.ie',
      phoneNumber: '+353 21 987 6543',
      interestReason: 'New store opening',
      rangeRequirements: 'Full beverage range',
      serviceNeeds: 'Twice weekly deliveries',
    },
    configuration: {
      storeId: 'CRK001',
      creditLimit: 15000,
    },
  },
  {
    retailerId: 3,
    statusId: 2, // Active
    name: 'Galway Gourmet',
    addressLine1: '78 Shop Street',
    city: 'Galway',
    postalCode: 'H91 X2Y3',
    country: 'Ireland',
    region: 'Connacht',
    latitude: 53.2707,
    longitude: -9.0568,
    vatnumber: 'IE5678901T',
    awrsnumber: 'AWRS00054321',
    progressStage: true,
    franchiseName: 'CFA',
    contactDetails: {
      name: 'Michael Walsh',
      jobTitle: 'Operations Manager',
      email: 'michael@galwaygourmet.ie',
      phoneNumber: '+353 91 123 4567',
    },
    configuration: {
      storeId: 'GAL001',
      externalIdentifier: 'EXT-GAL-001',
      creditLimit: 30000,
      catalogueId: 2,
      leadTime: 7,
    },
    deliveryLocations: [
      {
        id: 2,
        name: 'Main Store',
        addressLine1: '78 Shop Street',
        city: 'Galway',
        postalCode: 'H91 X2Y3',
        country: 'Ireland',
        contactName: 'Michael Walsh',
        contactNumber: '+353 91 123 4567',
      },
      {
        id: 3,
        name: 'Warehouse',
        addressLine1: 'Unit 5, Industrial Estate',
        city: 'Galway',
        postalCode: 'H91 W4Z5',
        country: 'Ireland',
        contactName: 'Tom Kelly',
        contactNumber: '+353 91 765 4321',
      },
    ],
  },
  {
    retailerId: 4,
    statusId: 3, // Suspended
    name: 'Belfast Beverages',
    addressLine1: '12 High Street',
    city: 'Belfast',
    postalCode: 'BT1 2AB',
    country: 'United Kingdom',
    region: 'Northern Ireland',
    latitude: 54.5973,
    longitude: -5.9301,
    progressStage: false,
    franchiseName: 'Orderly',
  },
];

const mockOrderCategories: Int32StringKeyValuePair[] = [
  { key: 1, value: 'Beverages' },
  { key: 2, value: 'Food' },
  { key: 3, value: 'Packaging' },
  { key: 4, value: 'Cleaning Supplies' },
  { key: 5, value: 'Equipment' },
];

const mockCatalogues: Int32StringKeyValuePair[] = [
  { key: 1, value: 'Core Range 2025' },
  { key: 2, value: 'Premium Selection' },
  { key: 3, value: 'Seasonal Specials' },
  { key: 4, value: 'Local Suppliers' },
];

/* ---- Mocked Client Implementation ---- */

export class OnboardingClientMocked implements IOnboardingClient {
  /**
   * Get page data for onboarding
   */
  async pagedata(): Promise<OnboardingPageData> {
    await this.delay(200);

    return {
      documentSkeleton: [...mockDocumentSkeleton],
      palletSizes: [...mockPalletSizes],
      containerSizes: [...mockContainerSizes],
    };
  }

  /**
   * Get retailers list
   */
  async retailers(body?: UserFranchiseList): Promise<RetailerListModel> {
    await this.delay(300);

    let filteredRetailers = [...mockRetailers];

    // Filter by franchise if specified and otherRegions is false
    if (body?.franchiseNames && body.franchiseNames.length > 0 && !body.otherRegions) {
      const filtered = filteredRetailers.filter(
        (r) => r.franchiseName && body.franchiseNames!.includes(r.franchiseName),
      );
      // Only apply filter if it returns results, otherwise show all
      if (filtered.length > 0) {
        filteredRetailers = filtered;
      }
    }

    if (body?.currentFranchiseName && !body.otherRegions) {
      const filtered = filteredRetailers.filter(
        (r) => r.franchiseName === body.currentFranchiseName,
      );
      // Only apply filter if it returns results, otherwise show all
      if (filtered.length > 0) {
        filteredRetailers = filtered;
      }
    }

    const retailerList: RetailerHeaderModel[] = filteredRetailers.map((r) => ({
      retailerId: r.retailerId,
      statusId: r.statusId,
      name: r.name,
      addressLine1: r.addressLine1,
      addressLine2: r.addressLine2,
      city: r.city,
      postalCode: r.postalCode,
      country: r.country,
      region: r.region,
      latitude: r.latitude,
      longitude: r.longitude,
      franchiseName: r.franchiseName,
    }));

    return { retailerList };
  }

  /**
   * Get active retailers by company ID
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getActiveRetailersByCompany(companyId?: number): Promise<RetailerIdentifierModel[]> {
    await this.delay(200);

    // Return active retailers (statusId === 2)
    const activeRetailers = mockRetailers
      .filter((r) => r.statusId === 2)
      .map((r) => ({
        storeId: r.configuration?.storeId,
        externalIdentifier: r.configuration?.externalIdentifier,
      }));

    return activeRetailers;
  }

  /**
   * Validate store ID for a retailer
   */
  async storevalidate(storeId?: string, retailerId?: number): Promise<boolean> {
    await this.delay(150);

    if (!storeId) return true;

    // Check if the store ID is already in use by another retailer
    const existingRetailer = mockRetailers.find(
      (r) =>
        r.configuration?.storeId === storeId &&
        r.retailerId !== retailerId,
    );

    return !existingRetailer; // Valid if not found
  }

  /**
   * Get catalogues for a franchise
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async catalogues(franchiseName?: string): Promise<OnboardingCataloguesModel> {
    await this.delay(200);

    return {
      orderCategories: [...mockOrderCategories],
      catalogues: [...mockCatalogues],
    };
  }

  /**
   * Get retailer onboarding data
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async retailer(retailerId?: number, body?: UserFranchiseList): Promise<RetailerOnboardingDataModel> {
    await this.delay(250);

    const retailer = mockRetailers.find((r) => r.retailerId === retailerId);

    if (!retailer) {
      // Return empty model for new retailer
      return {
        retailerId: undefined,
        statusId: 1,
        progressStage: false,
        currentDocuments: [...mockDocumentSkeleton],
      };
    }

    return { ...retailer };
  }

  /**
   * Update retailer onboarding data
   */
  async update(body?: RetailerOnboardingDataModel): Promise<number> {
    await this.delay(400);

    if (!body) {
      throw new Error('Retailer data is required');
    }

    const existingIndex = mockRetailers.findIndex((r) => r.retailerId === body.retailerId);

    if (existingIndex >= 0) {
      // Update existing retailer
      mockRetailers[existingIndex] = { ...mockRetailers[existingIndex], ...body };
      console.log('[OnboardingClientMocked] Updated retailer:', body.retailerId);
      return body.retailerId!;
    } else {
      // Create new retailer
      const newId = Math.max(...mockRetailers.map((r) => r.retailerId || 0)) + 1;
      const newRetailer = { ...body, retailerId: newId };
      mockRetailers.push(newRetailer);
      console.log('[OnboardingClientMocked] Created new retailer:', newId);
      return newId;
    }
  }

  /**
   * Delete a retailer
   */
  async delete(retailerId?: number): Promise<void> {
    await this.delay(300);

    if (!retailerId) {
      throw new Error('Retailer ID is required');
    }

    const index = mockRetailers.findIndex((r) => r.retailerId === retailerId);
    if (index >= 0) {
      mockRetailers.splice(index, 1);
      console.log('[OnboardingClientMocked] Deleted retailer:', retailerId);
    }
  }

  /**
   * Update shipped retailer orders
   */
  async updateShippedRetailerOrders(body?: RetailerEdnModel): Promise<string> {
    await this.delay(500);

    const orderCount = body?.orders?.length || 0;
    console.log('[OnboardingClientMocked] Updated shipped orders:', orderCount);

    return `Successfully processed ${orderCount} order(s)`;
  }

  /* ---- Helper Methods ---- */

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /* ---- Test Utilities ---- */

  /**
   * Get all retailers (for testing)
   */
  getAllRetailers(): RetailerOnboardingDataModel[] {
    return [...mockRetailers];
  }

  /**
   * Reset to initial mock data (for testing)
   */
  resetMockData(): void {
    // Reset to initial state - would need to store initial state separately
    console.log('[OnboardingClientMocked] Mock data reset');
  }
}
